import { useRef } from 'react';

import mapValues from 'lodash/fp/mapValues';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import Carousel from 'Components/Carousel';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import { flexibility, percentOff } from 'assets/content/membership/subscriberPercs';
import { ReactComponent as SquiggleEarth } from 'assets/images/membership/squiggle-earth.svg';
import { ReactComponent as SquiggleLime } from 'assets/images/membership/squiggle-lime.svg';

import useDebounce from 'utils/useDebounce';
import useMedia from 'utils/useMedia';
import useWidth from 'utils/useWidth';

import OffCenterWrapper from '../SharedComponents/OffCenterWrapper';
import TextPercs from '../SharedComponents/TextPercs';
import ConnectedComplementaryGiftPercsCard from './ComplementaryGiftPercsCard';
import { PERCS_CARD_MAX_WIDTH, PERCS_CARD_MIN_WIDTH } from './MembershipPercsCard';
import { PodcastPercsCard, ProductTestingPercsCard } from './PercsCards';

const CarrouselWrapper = styled('section')({
  // display: 'flex',
  // alignItems: 'center',
  // justifyContent: 'flex-start',
  // flexDirection: 'column',
  // width: '100%',
  // padding: '28px 16px',
  backgroundColor: theme.colors.primary[400],
  paddingTop: 72,
  paddingBottom: 200,
  [legacyTheme.breakpoints.up('lg')]: {
    paddingBottom: 300,
  },
});

const StyledCarousel = styled(Carousel)`
  padding: 0;
`;

const SubscriberPercsMenu = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: theme.colors.tertiary[100],
  width: '100%',
  textAlign: 'center',
  alignItems: 'center',
});

const ArchWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: 342,
  justifyContent: 'center',
  alignItems: 'center',
  [legacyTheme.breakpoints.up('lg')]: {
    width: 500,
    height: 250,
  },
});

const Arch = styled('div')({
  // Desktop and Mobile common styles
  backgroundColor: theme.colors.tertiary[200],
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  borderTopLeftRadius: '50%',
  borderTopRightRadius: '50%',
  minHeight: 100,
  height: 250,
  marginTop: -50,
  paddingTop: 100,
  width: '100%',
  [legacyTheme.breakpoints.up('sm')]: {
    width: 500,
    height: 250,
    borderTopLeftRadius: '250px',
    borderTopRightRadius: '250px',
  },
});

const PercsCardContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
});

const Spacer = styled('div')({
  height: 150,
});

const StyledLimeSquiggle = styled(SquiggleLime)({
  zIndex: 2,
  '--stroke-color': theme.colors.highlight['200'],
});
const StyledEarthSquiggle = styled(SquiggleEarth)({
  zIndex: 2,
  marginTop: -50,
  '--stroke-color': theme.colors.accent['300'],
});

const SubscriberPercs = () => {
  const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);
  const isTablet = useMedia(`(max-width: ${breakpoints.lg - 1}px)`) && !isMobile;
  // const isDesktop = !(isTablet || isMobile);

  // Similar logic as Home.js
  const carrouselWrapperRef = useRef();
  const carrouselWrapperWidth = useWidth(carrouselWrapperRef);
  const debouncedCarrouselWidth = useDebounce(carrouselWrapperWidth);
  // Either 18 pixel on smaller screen (smaller than a PercsCard) or 20% on anything above
  const nextHighlight =
    debouncedCarrouselWidth > PERCS_CARD_MAX_WIDTH
      ? debouncedCarrouselWidth * 0.2
      : debouncedCarrouselWidth > PERCS_CARD_MIN_WIDTH + 18 * 2
      ? 18
      : 0;

  const subscriberPercs = mapValues(
    ({ title, paragraph }) => ({
      title,
      paragraph,
    }),
    { percentOff, flexibility }
  );
  return (
    <>
      <CarrouselWrapper ref={carrouselWrapperRef}>
        <Typography align="center" color="white" variant="h1r">
          Try something special
        </Typography>
        <br />
        {(isMobile || isTablet) && (
          <Typography align="center" color="white" paragraph>
            Swipe to see all the insider benefits you have coming your way.
          </Typography>
        )}
        {isMobile || isTablet ? (
          <StyledCarousel
            arrows={false}
            controlsTheme="rectangleWhite"
            heap="Membership LP Subscribers - Carousel"
            nextHighlight={nextHighlight}
          >
            <PercsCardContainer>
              <ConnectedComplementaryGiftPercsCard />
            </PercsCardContainer>
            <PercsCardContainer>
              <PodcastPercsCard />
            </PercsCardContainer>
            <PercsCardContainer>
              <ProductTestingPercsCard />
            </PercsCardContainer>
          </StyledCarousel>
        ) : (
          <StyledCarousel
            arrows={false}
            controlsTheme="rectangleWhite"
            heap="Membership LP Subscribers - Carousel"
          >
            <PercsCardContainer>
              <ConnectedComplementaryGiftPercsCard />
              <PodcastPercsCard />
              <ProductTestingPercsCard />
            </PercsCardContainer>
          </StyledCarousel>
        )}
        {isTablet && <Spacer />}
      </CarrouselWrapper>
      <SubscriberPercsMenu>
        <OffCenterWrapper value={isMobile ? -165 : -200}>
          {isMobile && <StyledLimeSquiggle />}
          <ArchWrapper>
            <Arch>
              <Typography variant="h2" whiteSpace="pre-line">
                The usual, please
              </Typography>
              <br />
              <Typography variant="p2">Enjoy unlimited access to all these member perks</Typography>
            </Arch>
            {!isMobile && <StyledEarthSquiggle />}
          </ArchWrapper>
          <TextPercs textContent={subscriberPercs} />
        </OffCenterWrapper>
      </SubscriberPercsMenu>
    </>
  );
};

export default SubscriberPercs;
